import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Checkbox } from 'primereact/checkbox';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import StudentsListSkeletonLoader from '../../skeletons/StudentsListSkeletonLoader';
import ClassSectionFilter from '../Students/parts/ClassSectionFilter';
import { useOptions } from '../../stateManagers/hooks/useOptions';
import {
  StudentData,
  GatePassSettings,
  buildPreviewDocument,
  printGatePasses,
} from './parts/gatePassPrintService';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
import { GlobalTextArea } from '../../components/GlobalComponents/GlobalTextArea';

interface ClassData {
  id: string;
  class_name: string;
  sections: string[];
}

function useSchoolLogo() {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  useEffect(() => {
    apiClient.get('/options-files/school_logo')
      .then((res) => { setLogoUrl(res.data?.files?.[0]?.cdn_url ?? null); })
      .catch(() => {});
  }, []);
  return logoUrl;
}

// Fallback sample used for the live preview when no real student is available yet
const SAMPLE_STUDENT: StudentData = {
  id: 0,
  user_id: 0,
  name: 'John Doe',
  email_id: '',
  class_id: '',
  class_name: 'X',
  section: 'A',
  roll_no: '12',
  father_name: 'Richard Doe',
  father_no: '',
  profile_picture_url: null,
};

const hintTextStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '11px',
  color: '#6b7280',
  marginTop: '6px',
};

const GatePassGenerator: React.FC = () => {
  const toast = useRef<Toast>(null);
  const logoUrl = useSchoolLogo();
  const signatureInputRef = useRef<HTMLInputElement>(null);

  const [classes, setClasses] = useState<ClassData[]>([]);
  const [students, setStudents] = useState<StudentData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [examTitle, setExamTitle] = useState<string>('');
  const [sessionLabel, setSessionLabel] = useState<string>('2026-2027');
  const [examString, setExamString] = useState<string>('');
  const [websiteUrl, setWebsiteUrl] = useState<string>('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<number>>(new Set());

  // Watermark controls
  const [watermarkText, setWatermarkText] = useState<string>('');
  const [watermarkImageUrl, setWatermarkImageUrl] = useState<string>('');
  const [watermarkWidth, setWatermarkWidth] = useState<number>(60);
  const [watermarkRepeat, setWatermarkRepeat] = useState<boolean>(false);
  const [watermarkAngle, setWatermarkAngle] = useState<number>(0);

  // Signature controls
  const [signatureDataUrl, setSignatureDataUrl] = useState<string>('');
  const [signatureWidth, setSignatureWidth] = useState<number>(80); // percent of coordinator box width

  const { options: schoolOptions } = useOptions<string[]>(['school_name', 'current_session']);
  const schoolName = (schoolOptions['school_name']?.data?.[0] ?? 'School') as string;

  useEffect(() => {
    if (selectedClass && shouldFetchStudents()) {
      fetchStudents();
    } else {
      setStudents([]);
    }
  }, [selectedClass, selectedSection]);

  const handleClassesLoaded = (loadedClasses: ClassData[]) => {
    setClasses(loadedClasses);
  };

  const selectedClassHasSections = (): boolean => {
    if (!selectedClass) return false;
    const classData = classes.find(c => c.id === selectedClass);
    return classData ? Boolean(classData.sections && classData.sections.length > 0) : false;
  };

  const shouldFetchStudents = (): boolean => {
    if (!selectedClass) return false;
    if (selectedClassHasSections()) return selectedSection !== null;
    return true;
  };

  const fetchStudents = async (): Promise<void> => {
    if (!selectedClass || !shouldFetchStudents()) {
      setStudents([]);
      setSelectedStudentIds(new Set());
      return;
    }
    setLoading(true);
    try {
      let params: any = { page: 1, limit: 100, class_id: selectedClass };
      if (selectedClassHasSections() && selectedSection) params.section = selectedSection;

      const response = await apiClient.get('/get-students-list/class-section', { params });
      const data = response.data;

      if (data.status === 'success') {
        setStudents(data.data);
        setSelectedStudentIds(new Set((data.data as StudentData[]).map(s => s.id)));
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch students', life: 4000 });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Network Error', detail: err.response?.data?.message || 'An error occurred', life: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (classId: string | null) => setSelectedClass(classId);
  const handleSectionChange = (section: string | null) => setSelectedSection(section);

  const toggleStudent = (id: number) => {
    setSelectedStudentIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedStudentIds(new Set(students.map(s => s.id)));
  const deselectAll = () => setSelectedStudentIds(new Set());
  const allSelected = students.length > 0 && selectedStudentIds.size === students.length;
  const someSelected = selectedStudentIds.size > 0 && selectedStudentIds.size < students.length;

  // Handle signature file upload
  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.current?.show({ severity: 'warn', summary: 'Invalid File', detail: 'Please upload an image file', life: 3000 });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setSignatureDataUrl(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeSignature = () => {
    setSignatureDataUrl('');
    if (signatureInputRef.current) signatureInputRef.current.value = '';
  };

  // ── Live preview ──────────────────────────────────────────────────────
  // Uses one entry only: the first selected student if available, otherwise
  // the first loaded student, otherwise a sample placeholder. Rebuilt via
  // the same service functions used for the real print job, so it's always
  // an exact, original preview of the printed output.
  const previewStudent: StudentData = useMemo(() => {
    const firstSelected = students.find(s => selectedStudentIds.has(s.id));
    return firstSelected ?? students[0] ?? SAMPLE_STUDENT;
  }, [students, selectedStudentIds]);

  const previewClassName = useMemo(() => {
    if (selectedClass) return classes.find(c => c.id === selectedClass)?.class_name ?? 'Unknown';
    return SAMPLE_STUDENT.class_name;
  }, [classes, selectedClass]);

  const gatePassSettings: GatePassSettings = {
    schoolName,
    logoUrl,
    orientation,
    examTitle,
    sessionLabel,
    examString,
    websiteUrl,
    watermarkText,
    watermarkImageUrl,
    watermarkWidth,
    watermarkRepeat,
    watermarkAngle,
    signatureDataUrl,
    signatureWidth,
  };

  const previewDoc = useMemo(
    () => buildPreviewDocument(previewStudent, previewClassName, gatePassSettings),
    [
      previewStudent,
      previewClassName,
      schoolName,
      logoUrl,
      orientation,
      examTitle,
      sessionLabel,
      examString,
      websiteUrl,
      watermarkText,
      watermarkImageUrl,
      watermarkWidth,
      watermarkRepeat,
      watermarkAngle,
      signatureDataUrl,
      signatureWidth,
    ],
  );

  const generateGatePasses = () => {
    const selectedStudents = students.filter(s => selectedStudentIds.has(s.id));
    if (selectedStudents.length === 0) {
      toast.current?.show({ severity: 'warn', summary: 'No Students', detail: 'No students selected to generate gate passes for', life: 4000 });
      return;
    }

    const className = classes.find(c => c.id === selectedClass)?.class_name || 'Unknown';
    const opened = printGatePasses(selectedStudents, className, gatePassSettings);

    if (opened) {
      toast.current?.show({ severity: 'success', summary: 'Success', detail: `Generated ${selectedStudents.length} gate pass(es)`, life: 4000 });
    } else {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Unable to open print window. Check browser settings.', life: 5000 });
    }
  };

  const orientationOptions = [
    { label: '📄 Portrait', value: 'portrait' },
    { label: '📃 Landscape', value: 'landscape' },
  ];

  const seekbarContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const seekbarLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    color: '#9ca3af',
    minWidth: '28px',
  };

  const seekbarTitleStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#1e3a8a',
    marginBottom: '10px',
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        {/* Header */}
        <GlobalHeaderTitle
          title="Gate Pass Generator"
          subtitle="Generate printable gate passes / hall tickets for students"
          icon="pi pi-ticket"
          buttons={[
            {
              label: `Generate Gate Passes (${selectedStudentIds.size})`,
              icon: 'pi pi-file-pdf',
              onClick: generateGatePasses,
              disabled: selectedStudentIds.size === 0 || loading,
              style: {
                background: selectedStudentIds.size > 0 && !loading
                  ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                  : undefined,
              },
            },
          ]}
        />

        {/* Class Section Filter */}
        <ClassSectionFilter
          selectedClass={selectedClass}
          selectedSection={selectedSection}
          onClassChange={handleClassChange}
          onSectionChange={handleSectionChange}
          onClassesLoaded={handleClassesLoaded}
        />

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.3fr) minmax(320px, 1fr)',
          gap: '20px',
          alignItems: 'start',
        }}>
          {/* Settings Panel */}
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.12)',
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '16px',
            }}>

              {/* Orientation */}
              <GlobalDropdownField
                label="Page Orientation"
                value={orientation}
                onChange={(val) => setOrientation(val)}
                options={orientationOptions}
                placeholder="Select Orientation"
              />

              {/* Exam Title (badge) */}
              <GlobalInputField
                label="Exam Type / Badge Label"
                value={examTitle}
                onChange={e => setExamTitle(e.target.value)}
                placeholder="e.g. Annual Examination 2026-27"
              />

              {/* Session */}
              <GlobalInputField
                label="Session Year"
                value={sessionLabel}
                onChange={e => setSessionLabel(e.target.value)}
                placeholder="e.g. 2026-2027"
              />

              {/* Website URL */}
              <GlobalInputField
                label="School Website URL"
                value={websiteUrl}
                onChange={e => setWebsiteUrl(e.target.value)}
                placeholder="e.g. www.schoolname.edu.in"
              />

              {/* Watermark Text */}
              <div>
                <GlobalTextArea
                  label="Watermark Text"
                  value={watermarkText}
                  onChange={e => setWatermarkText(e.target.value)}
                  placeholder={`e.g. ${schoolName.split(' ').slice(0, Math.ceil(schoolName.split(' ').length / 2)).join(' ')}<br>${schoolName.split(' ').slice(Math.ceil(schoolName.split(' ').length / 2)).join(' ')}`}
                  rows={2}
                  autoResize={false}
                />
                <span style={hintTextStyle}>Use &lt;br&gt; for new line; leave blank = no watermark</span>
              </div>

              {/* Watermark Image URL */}
              <div>
                <GlobalInputField
                  label="Watermark Image URL"
                  value={watermarkImageUrl}
                  onChange={e => setWatermarkImageUrl(e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
                <span style={hintTextStyle}>Overrides text watermark if provided</span>
              </div>

              {/* Watermark Width Seekbar */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={seekbarTitleStyle}>
                  Watermark Width: <span style={{ color: '#374151', fontWeight: '700' }}>{watermarkWidth}%</span>
                </label>
                <div style={seekbarContainerStyle}>
                  <span style={seekbarLabelStyle}>10%</span>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    step={5}
                    value={watermarkWidth}
                    onChange={e => setWatermarkWidth(Number(e.target.value))}
                    style={{ flex: 1, height: '6px', accentColor: '#1e40af', cursor: 'pointer' }}
                  />
                  <span style={{ ...seekbarLabelStyle, minWidth: '32px' }}>100%</span>
                </div>
              </div>

              {/* Watermark Repeat Toggle */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}>
                  <Checkbox
                    checked={watermarkRepeat}
                    onChange={e => setWatermarkRepeat(Boolean(e.checked))}
                  />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e3a8a' }}>
                    Repeat watermark across the card
                  </span>
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>
                    (tiles it in a pattern instead of showing it once, centered)
                  </span>
                </label>
              </div>

              {/* Watermark Rotation Seekbar */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={seekbarTitleStyle}>
                  Watermark Rotation: <span style={{ color: '#374151', fontWeight: '700' }}>{watermarkAngle}°</span>
                </label>
                <div style={seekbarContainerStyle}>
                  <span style={seekbarLabelStyle}>-90°</span>
                  <input
                    type="range"
                    min={-90}
                    max={90}
                    step={5}
                    value={watermarkAngle}
                    onChange={e => setWatermarkAngle(Number(e.target.value))}
                    style={{ flex: 1, height: '6px', accentColor: '#1e40af', cursor: 'pointer' }}
                  />
                  <span style={{ ...seekbarLabelStyle, minWidth: '32px' }}>90°</span>
                </div>
              </div>

              {/* ── Signature Upload + Width Seekbar ── */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={seekbarTitleStyle}>
                  Coordinator Signature{' '}
                  <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '11px' }}>
                    (image will appear above the coordinator line)
                  </span>
                </label>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                  {/* Upload / Remove buttons */}
                  <input
                    ref={signatureInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleSignatureUpload}
                    style={{ display: 'none' }}
                    id="signature-upload"
                  />
                  <GlobalButton
                    label="Upload Signature"
                    icon="pi pi-upload"
                    size="small"
                    onClick={() => signatureInputRef.current?.click()}
                  />
                  {signatureDataUrl && (
                    <GlobalButton
                      label="Remove"
                      icon="pi pi-times"
                      variant="danger"
                      size="small"
                      onClick={removeSignature}
                    />
                  )}

                  {/* Signature preview box */}
                  {signatureDataUrl ? (
                    <div style={{
                      border: '1.5px solid #c7d7f9',
                      borderRadius: '8px',
                      background: '#f8fafc',
                      padding: '6px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '120px',
                      maxWidth: '200px',
                      height: '50px',
                    }}>
                      <img
                        src={signatureDataUrl}
                        alt="Signature preview"
                        style={{
                          maxHeight: '38px',
                          width: `${signatureWidth}%`,
                          objectFit: 'contain',
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      border: '1.5px dashed #d1d5db',
                      borderRadius: '8px',
                      background: '#fafafa',
                      padding: '6px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '50px',
                      color: '#9ca3af',
                      fontSize: '12px',
                      fontStyle: 'italic',
                    }}>
                      No signature uploaded
                    </div>
                  )}
                </div>

                {/* Signature Width Seekbar */}
                <div style={{ marginTop: '14px' }}>
                  <label style={seekbarTitleStyle}>
                    Signature Width: <span style={{ color: '#374151', fontWeight: '700' }}>{signatureWidth}%</span>
                    <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '11px', marginLeft: '6px' }}>
                      (relative to coordinator box)
                    </span>
                  </label>
                  <div style={seekbarContainerStyle}>
                    <span style={seekbarLabelStyle}>20%</span>
                    <input
                      type="range"
                      min={20}
                      max={100}
                      step={5}
                      value={signatureWidth}
                      onChange={e => setSignatureWidth(Number(e.target.value))}
                      style={{ flex: 1, height: '6px', accentColor: '#1e40af', cursor: 'pointer' }}
                    />
                    <span style={{ ...seekbarLabelStyle, minWidth: '32px' }}>100%</span>
                  </div>
                </div>
              </div>

              {/* Exam String */}
              <div style={{ gridColumn: '1 / -1' }}>
                <GlobalTextArea
                  label="Exam Description / Note (shown on gate pass)"
                  value={examString}
                  onChange={e => setExamString(e.target.value)}
                  placeholder="e.g. This admit card is valid for the Annual Examination commencing from 01-05-2026. Report to the centre 30 minutes before exam time."
                  rows={3}
                  autoResize={false}
                />
              </div>
            </div>
          </div>

          {/* Live Preview Panel — single entry, rebuilt from the same
              print service so it's always an exact, original preview */}
          <div style={{
            position: 'sticky',
            top: '16px',
            background: 'white',
            borderRadius: '16px',
            padding: '16px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(30, 64, 175, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.12)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <i className="pi pi-eye" style={{ fontSize: '14px', color: '#1e40af' }} />
              <span style={{ fontSize: '13px', fontWeight: '700', color: '#1e40af', letterSpacing: '0.02em' }}>
                Live Preview
              </span>
              <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                ({previewStudent === SAMPLE_STUDENT ? 'sample data' : previewStudent.name})
              </span>
            </div>
            <div style={{
              borderRadius: '10px',
              overflow: 'hidden',
              border: '1px solid #e5e7eb',
              background: '#eef2f9',
            }}>
              <iframe
                key={orientation}
                title="Gate Pass Live Preview"
                srcDoc={previewDoc}
                style={{
                  width: '100%',
                  height: orientation === 'landscape' ? '340px' : '460px',
                  border: 'none',
                  display: 'block',
                }}
              />
            </div>
          </div>
        </div>

        {/* Students Preview */}
        {loading ? (
          <StudentsListSkeletonLoader />
        ) : (
          <div style={{
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
          }}>
            {students.length > 0 ? (
              <>
                {/* Info bar */}
                <div style={{
                  padding: '14px 20px',
                  borderBottom: '1px solid #f3f4f6',
                  background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
                  display: 'flex',
                  gap: '12px',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '8px',
                      background: 'linear-gradient(135deg, rgba(5,150,105,0.1) 0%, rgba(16,185,129,0.08) 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <i className="pi pi-users" style={{ fontSize: '14px', color: '#059669' }} />
                    </div>
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                      {students.length} student{students.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {examTitle && (
                    <div style={{
                      padding: '5px 12px', borderRadius: '8px',
                      background: 'rgba(245,158,11,0.1)',
                      border: '1px solid rgba(245,158,11,0.2)',
                    }}>
                      <span style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '600' }}>{examTitle}</span>
                    </div>
                  )}
                  <div style={{
                    padding: '5px 12px', borderRadius: '8px',
                    background: 'rgba(99,102,241,0.1)',
                    border: '1px solid rgba(99,102,241,0.2)',
                  }}>
                    <span style={{ fontSize: '13px', color: '#6366f1', fontWeight: '600' }}>
                      {selectedStudentIds.size} / {students.length} selected
                    </span>
                  </div>
                </div>

                {/* Students grid */}
                <div style={{ padding: '20px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '14px',
                    flexWrap: 'wrap',
                    gap: '10px',
                  }}>
                    <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e40af', margin: 0 }}>
                      Students ({students.length})
                    </h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <label style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151',
                        userSelect: 'none',
                      }}>
                        <Checkbox
                          checked={allSelected}
                          onChange={e => e.checked ? selectAll() : deselectAll()}
                          style={{ borderColor: someSelected ? '#f59e0b' : undefined }}
                        />
                        {allSelected ? 'Deselect All' : someSelected ? `${selectedStudentIds.size} selected` : 'Select All'}
                      </label>
                      <GlobalButton
                        label="All"
                        icon="pi pi-check"
                        variant="success"
                        size="small"
                        rounded={false}
                        onClick={selectAll}
                      />
                      <GlobalButton
                        label="None"
                        icon="pi pi-times"
                        variant="danger"
                        size="small"
                        rounded={false}
                        onClick={deselectAll}
                      />
                    </div>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                    gap: '14px',
                    maxHeight: '420px',
                    overflowY: 'auto',
                  }}>
                    {students.map(student => {
                      const isChecked = selectedStudentIds.has(student.id);
                      return (
                        <div
                          key={student.id}
                          onClick={() => toggleStudent(student.id)}
                          style={{
                            border: isChecked ? '2px solid #1e40af' : '1.5px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '14px',
                            background: isChecked ? '#f0f4ff' : '#fafbfc',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            position: 'relative',
                            userSelect: 'none',
                          }}
                        >
                          <div
                            style={{ position: 'absolute', top: '10px', right: '10px' }}
                            onClick={e => { e.stopPropagation(); toggleStudent(student.id); }}
                          >
                            <Checkbox checked={isChecked} onChange={() => toggleStudent(student.id)} />
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', paddingRight: '28px' }}>
                            {student.profile_picture_url ? (
                              <img
                                src={student.profile_picture_url}
                                alt={student.name}
                                style={{
                                  width: '44px', height: '44px', borderRadius: '8px',
                                  objectFit: 'cover',
                                  border: isChecked ? '2px solid #1e40af' : '2px solid #e5e7eb',
                                  transition: 'border-color 0.15s',
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '44px', height: '44px', borderRadius: '8px',
                                background: isChecked
                                  ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)'
                                  : 'linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '18px', fontWeight: '600', color: 'white',
                                transition: 'background 0.15s',
                              }}>
                                {student.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                fontWeight: '600', fontSize: '14px',
                                color: isChecked ? '#1e40af' : '#1f2937',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {student.name}
                              </div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>Roll: {student.roll_no}</div>
                            </div>
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.6 }}>
                            <div><strong>Class:</strong> {student.class_name}{student.section ? ` - ${student.section}` : ''}</div>
                            <div><strong>Father:</strong> {student.father_name}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: '60px 20px', minHeight: '360px',
              }}>
                <div style={{
                  width: '56px', height: '56px',
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
                  borderRadius: '14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: '20px',
                  boxShadow: '0 6px 18px rgba(59,130,246,0.2)',
                }}>
                  <i
                    className={
                      !selectedClass ? 'pi pi-filter'
                        : selectedClassHasSections() && !selectedSection ? 'pi pi-list'
                        : 'pi pi-inbox'
                    }
                    style={{ fontSize: '25px', color: 'white' }}
                  />
                </div>
                <h3 style={{ margin: '0 0 8px', color: '#1e40af', fontSize: '20px', fontWeight: '700', textAlign: 'center' }}>
                  {!selectedClass ? 'No Class Selected'
                    : selectedClassHasSections() && !selectedSection ? 'No Section Selected'
                    : 'No Students Found'}
                </h3>
                <p style={{ margin: 0, color: '#64748b', fontSize: '14px', textAlign: 'center', maxWidth: '380px', lineHeight: 1.6 }}>
                  {!selectedClass ? 'Please select a class from the filter above to load students'
                    : selectedClassHasSections() && !selectedSection ? 'Please select a section to load students for this class'
                    : 'No students enrolled in this class/section yet'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default GatePassGenerator;